import { useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  AppWindow,
  FolderOpen,
  Home,
  Inbox,
  Package,
  User,
  MoreHorizontal,
  Users,
  X,
} from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { apiClient } from '@/lib/api-client'
import { cn } from '@/lib/utils'

const adminTabs = [
  { name: 'Home', href: '/', icon: Home, end: true },
  { name: 'Devices', href: '/devices', icon: Package },
  { name: 'Requests', href: '/requests', icon: Inbox },
  { name: 'Software', href: '/software', icon: AppWindow },
]

const employeeTabs = [
  { name: 'Devices', href: '/', icon: Package, end: true },
  { name: 'Requests', href: '/requests', icon: Inbox },
  { name: 'Software', href: '/software', icon: AppWindow },
  { name: 'Profile', href: '/profile', icon: User },
]

const moreLinks = [
  { name: 'Projects', href: '/projects', icon: FolderOpen },
  { name: 'Users', href: '/users', icon: Users },
  { name: 'Profile', href: '/profile', icon: User },
]

export function BottomNav() {
  const { user } = useAuth()
  const location = useLocation()
  const isAdmin = user?.role === 'Admin'
  const tabs = isAdmin ? adminTabs : employeeTabs
  const [moreOpen, setMoreOpen] = useState(false)
  const moreActive = ['/projects', '/users', '/profile'].some((path) => location.pathname === path || location.pathname.startsWith(`${path}/`))

  const { data: openRequests = 0 } = useQuery({
    queryKey: ['device-requests', 'pending-count', user?.id],
    enabled: !!user,
    queryFn: async () => {
      const response = await apiClient.get('/device-requests', { params: { status: 'pending' } })
      return (response.data || []).length
    },
  })

  return (
    <>
      {moreOpen && (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-slate-900/40 lg:hidden"
          aria-label="Close more menu"
          onClick={() => setMoreOpen(false)}
        />
      )}
      {moreOpen && (
        <div className="fixed inset-x-3 bottom-[calc(4.25rem+env(safe-area-inset-bottom))] z-50 overflow-hidden rounded-2xl border border-border bg-card shadow-xl lg:hidden">
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <p className="text-sm font-semibold">More</p>
            <button type="button" className="rounded-md p-1 text-muted-foreground" onClick={() => setMoreOpen(false)} aria-label="Close">
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="p-2">
            {moreLinks.map((item) => (
              <NavLink
                key={item.href}
                to={item.href}
                onClick={() => setMoreOpen(false)}
                className={({ isActive }) => cn(
                  'flex h-12 items-center gap-3 rounded-xl px-3 text-sm',
                  isActive ? 'bg-primary/10 font-medium text-primary' : 'text-foreground',
                )}
              >
                <item.icon className="h-5 w-5" />
                {item.name}
              </NavLink>
            ))}
          </div>
        </div>
      )}
      <nav
        className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-card/95 backdrop-blur lg:hidden"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        aria-label="Primary"
      >
        <div className={cn('grid h-16', isAdmin ? 'grid-cols-5' : 'grid-cols-4')}>
          {tabs.map((item) => (
            <NavLink
              key={item.name}
              to={item.href}
              end={item.end}
              onClick={() => setMoreOpen(false)}
              className={({ isActive }) => cn(
                'relative flex flex-col items-center justify-center gap-1 text-[11px] font-medium',
                isActive ? 'text-primary' : 'text-muted-foreground',
              )}
            >
              <item.icon className="h-5 w-5" />
              {item.name}
              {item.href === '/requests' && openRequests > 0 && (
                <span className="absolute right-[22%] top-1.5 min-w-4 rounded-full bg-primary px-1 text-center text-[10px] font-semibold leading-4 text-primary-foreground">
                  {openRequests}
                </span>
              )}
            </NavLink>
          ))}
          {isAdmin && (
            <button
              type="button"
              onClick={() => setMoreOpen((open) => !open)}
              className={cn(
                'flex flex-col items-center justify-center gap-1 text-[11px] font-medium',
                moreOpen || moreActive ? 'text-primary' : 'text-muted-foreground',
              )}
            >
              <MoreHorizontal className="h-5 w-5" />
              More
            </button>
          )}
        </div>
      </nav>
    </>
  )
}
